import { colorsPalettes } from "@similarweb/styles";
import { Button, Tooltip } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { useCountries } from "app/pages/digital-marketing/monitor-and-protect/services/country/use-countries";
import { campaignWizardTracking } from "app/pages/digital-marketing/monitor-and-protect/services/tracking/campaign-wizard";
import { Dot } from "app/pages/digital-marketing/monitor-and-protect/shared/ui/dot";
import { TruncateTextWithTooltip } from "app/pages/digital-marketing/monitor-and-protect/shared/ui/typography/texts";

import type { IImportedKeywords } from "../../keywords-edit";
import { KeywordsEdit } from "../../keywords-edit";
import { CAMPAIGN_WIZARD_TRANSLATIONS } from "../translations";

interface IKeywordsStepProps {
    manualKeywords: string[];
    uniqueKeywords: string[];
    hasDuplicates: boolean;
    importSources: IImportedKeywords[];
    onManualInput: (keywords: string[]) => void;
    onImport: (importedKeywords: IImportedKeywords) => void;
    onRemoveImport: (importId: string) => void;
    onImportError?: () => void;
    selectedCountries: number[];
}

interface IKeywordsStepHeaderProps {
    currentStep: number;
    totalSteps: number;
}

interface IKeywordsStepFooterProps {
    selectedCountries: number[];
    deduplicatedKeywordsCount: number;
    onBack: () => void;
    onNext: () => void;
    selectedDomain: string;
    isCrawlLimitValid: boolean;
}

export const KeywordsStepHeader = ({ currentStep, totalSteps }: IKeywordsStepHeaderProps) => {
    return (
        <Stack gap="8px">
            <Text
                fontVariant="input2"
                color={colorsPalettes.carbon["300"]}
            >
                {CAMPAIGN_WIZARD_TRANSLATIONS.common.stepTitle.translate({ currentStep, totalSteps })}
            </Text>
            <Text
                fontVariant="h5_2"
                color={colorsPalettes.carbon["500"]}
            >
                {CAMPAIGN_WIZARD_TRANSLATIONS.keywords.header.translate()}
            </Text>
        </Stack>
    );
};

export const KeywordsStepFooter = ({
    deduplicatedKeywordsCount,
    onBack,
    onNext,
    selectedDomain,
    selectedCountries,
    isCrawlLimitValid,
}: IKeywordsStepFooterProps) => {
    const { getCountryById } = useCountries();

    const getCountryNames = (countryIds: number[]) => {
        return countryIds.map((id) => getCountryById(id)?.text).filter(Boolean);
    };

    const countryNames = getCountryNames(selectedCountries);
    const [first, ...rest] = countryNames;

    return (
        <>
            <Flex
                gap="8px"
                alignItems="center"
            >
                <Text
                    fontVariant="subtitle2"
                    color={colorsPalettes.carbon["400"]}
                >
                    {CAMPAIGN_WIZARD_TRANSLATIONS.keywords.monitoringFor.translate()}
                </Text>
                <TruncateTextWithTooltip
                    fontVariant="input2"
                    lineClamp={1}
                    maxWidth="100px"
                    color={colorsPalettes.carbon["400"]}
                >
                    {selectedDomain}
                </TruncateTextWithTooltip>
                <Dot />
                <Text
                    fontVariant="input2"
                    color={colorsPalettes.carbon["400"]}
                >
                    {first}
                </Text>
                {rest.length > 0 ? (
                    <Tooltip content={rest.join(", ")}>
                        <Text
                            fontVariant="input2"
                            cursor="pointer"
                            color={colorsPalettes.carbon["400"]}
                        >
                            {CAMPAIGN_WIZARD_TRANSLATIONS.keywords.additionalCountries.translate({
                                count: rest.length,
                            })}
                        </Text>
                    </Tooltip>
                ) : null}
            </Flex>
            <Flex gap="4px">
                <Button
                    type="flat"
                    onClick={onBack}
                >
                    {CAMPAIGN_WIZARD_TRANSLATIONS.keywords.back.translate()}
                </Button>
                <Button
                    onClick={() => {
                        campaignWizardTracking.onStartMonitoring(deduplicatedKeywordsCount);
                        onNext();
                    }}
                    isDisabled={deduplicatedKeywordsCount === 0 || !isCrawlLimitValid}
                >
                    {CAMPAIGN_WIZARD_TRANSLATIONS.keywords.startMonitoring.translate()}
                </Button>
            </Flex>
        </>
    );
};

export const KeywordsStep = ({
    manualKeywords,
    importSources,
    onManualInput,
    onImport,
    onRemoveImport,
    onImportError,
    uniqueKeywords,
    hasDuplicates,
    selectedCountries,
}: IKeywordsStepProps) => {
    const handleImportCSVClick = () => {
        campaignWizardTracking.onImportCSVClick();
    };

    const handleImportKeywordListClick = () => {
        campaignWizardTracking.onImportKeywordListClick();
    };

    const handleCSVError = () => {
        campaignWizardTracking.onCSVError();
    };

    return (
        <KeywordsEdit
            manualKeywords={manualKeywords}
            uniqueKeywords={uniqueKeywords}
            hasDuplicates={hasDuplicates}
            importSources={importSources}
            onManualInput={onManualInput}
            onImport={onImport}
            onRemoveImport={onRemoveImport}
            onImportError={onImportError}
            selectedCountries={selectedCountries}
            onImportCSVClick={handleImportCSVClick}
            onImportKeywordListClick={handleImportKeywordListClick}
            onCSVError={handleCSVError}
        />
    );
};